import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosProgressEvent } from 'axios';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { BulkUploadResponse } from '@schemas/api';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentCompleted: number;
}

/**
 * Universal document upload hook using Temporal workflows.
 * Accepts one or more files and submits each to its own processing workflow.
 *
 * @param documentType - Optional document type. If not provided, system will auto-classify.
 */
export const useUploadDocument = (documentType?: "purchase_order" | 'receipt' | 'invoice' | null) => {
  const queryClient = useQueryClient();

  return useMutation<BulkUploadResponse, Error, File[], UploadProgress>({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      const authState = useAuthStore.getState();
      const activeTenantId = authState.activeTenantId ?? authState.currentUser?.active_tenant_id ?? null;
      for (const file of files) {
        formData.append('files', file);
      }
      if (documentType) {
        formData.append('document_type', documentType);
      }
      if (activeTenantId) {
        formData.append('active_tenant_id', activeTenantId);
      }

      const { data } = await apiClient.post<BulkUploadResponse>(
        '/documents/upload-and-process',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(activeTenantId ? { 'X-Active-Tenant-ID': activeTenantId } : {}),
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            console.log(`Upload progress: ${percentCompleted}%`);
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['approved'] });
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    },
  });
};

/**
 * Auto-classification hook - uploads without specifying document type.
 * The backend will automatically classify each document.
 */
export const useUploadDocumentAutoClassify = () => {
  return useUploadDocument(null);
};

/**
 * Legacy hooks for backward compatibility.
 * These are deprecated and should use useUploadDocument instead.
 */
export const useUploadPO = () => {
  return useUploadDocument("purchase_order");
};

export const useUploadReceipt = () => {
  return useUploadDocument('receipt');
};

export const useUploadInvoice = () => {
  return useUploadDocument('invoice');
};
